import React, { useState, useMemo, useCallback, memo, useEffect } from "react";
import {
  Button, Input, Select, Card, Spin, Pagination, Badge, Modal, Form,
  Input as AntInput, Tooltip, Tag, Tabs, Drawer, Avatar, message, Steps, Checkbox,
  Collapse,
} from "antd";
import {
  SearchOutlined, ReloadOutlined, FilterOutlined, MenuOutlined, CheckOutlined,
  ClockCircleOutlined, UserOutlined, CloseCircleOutlined, FileTextOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { usePatientSessions, useUpdateSessionStatus } from "../../../hooks/usePatientSessions";
import { getImg } from "../../../Api/getImg";
import { getUserReport } from "../../../Api/getUserReport";
import { formatDateTime } from "../../../utils/helpers";
import StartSessionModal from "./StartSessionModal";

const { Option } = Select;
const { TextArea } = AntInput;
const { TabPane } = Tabs;
const { Step } = Steps;

const statusConfig = {
  0: { label: "Pending", color: "warning", bgLight: "bg-amber-50", borderColor: "border-amber-200", textColor: "text-yellow-600", badgeStatus: "warning" },
  1: { label: "Cancelled", color: "error", bgLight: "bg-red-50", borderColor: "border-red-200", textColor: "text-red-600", badgeStatus: "error" },
  2: { label: "Completed", color: "success", bgLight: "bg-green-50", borderColor: "border-green-200", textColor: "text-green-600", badgeStatus: "success" },
  3: { label: "Skipped", color: "default", bgLight: "bg-gray-50", borderColor: "border-gray-200", textColor: "text-gray-600", badgeStatus: "default" },
};

const useResponsive = () => {
  const [breakpoint, setBreakpoint] = useState("lg");
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      if (width < 640) { setBreakpoint("sm"); setIsMobile(true); setIsTablet(false); }
      else if (width < 768) { setBreakpoint("md"); setIsMobile(false); setIsTablet(true); }
      else if (width < 1024) { setBreakpoint("lg"); setIsMobile(false); setIsTablet(true); }
      else if (width < 1280) { setBreakpoint("xl"); setIsMobile(false); setIsTablet(false); }
      else { setBreakpoint("2xl"); setIsMobile(false); setIsTablet(false); }
    };
    updateBreakpoint();
    window.addEventListener("resize", updateBreakpoint);
    return () => window.removeEventListener("resize", updateBreakpoint);
  }, []);

  return { breakpoint, isMobile, isTablet };
};

const now = dayjs();

const SessionCard = memo(({ session, index, onStartSession, onCancel, isMobile,setToComplete }) => {
  const status = statusConfig[session.status] || statusConfig[0];
  const sessionDate = dayjs(session.sessionDate);

  return (
    <div
      className={`group relative bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border ${status.borderColor} overflow-hidden ${isMobile ? "mb-4" : ""}`}
      style={{ animationDelay: `${index * 100}ms`, animation: "fadeInUp 0.6s ease-out forwards" }}
    >
      <div className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10">
        <Badge
          status={status.badgeStatus}
          text={
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${status.bgLight} border ${status.borderColor} ${status.textColor} shadow-sm`}>
              {status.label}
            </span>
          }
        />
      </div>

      <div className="p-4 sm:p-6">
        <div className={`flex items-center mb-3 sm:mb-4 ${isMobile ? "flex-wrap" : ""} pt-3`}>
          <Avatar size={isMobile ? 40 : 48} icon={<UserOutlined />} className="bg-gradient-to-r from-primary to-primary_hover flex-shrink-0" />
          <div className="ml-3 sm:ml-4 min-w-0 flex-1">
            <h3 className={`font-bold text-gray-800 ${isMobile ? "text-base" : "text-lg"} mb-1`}>{session.patientName}</h3>
          </div>
        </div>

        <div className="p-3 sm:p-4 rounded-lg sm:rounded-xl mb-3 sm:mb-4 bg-gray-50 border border-gray-200">
          <div className="flex items-center mb-2">
            <Avatar
              size={isMobile ? 20 : 32}
              src={session.specialistPhotoId ? getImg(session.specialistPhotoId) : null}
              icon={!session.specialistPhotoId ? <UserOutlined /> : null}
              className="bg-gradient-to-r from-primary to-primary_hover flex-shrink-0"
            />
            <span className={`mx-2 font-semibold text-gray-800 ${isMobile ? "text-sm" : "text-base"}`}>
              dr. {session.specialistName}
            </span>
          </div>
          <div className="flex items-center text-xs sm:text-sm text-gray-600 mb-2">
            <ClockCircleOutlined className="mr-2 flex-shrink-0" />
            <span>{sessionDate.format("MMM D, YYYY")} - {dayjs(session.startTime, "HH:mm:ss").format("h:mm A")}</span>
          </div>
          {session.noteText && (
            <div className="text-xs text-gray-600 mt-2">
              <span className="font-semibold">Note:</span> {session.noteText}
            </div>
          )}
          {session.cancelReason && (
            <div className="text-xs text-red-600 mt-2">
              <span className="font-semibold">Cancellation Reason:</span> {session.cancelReason}
            </div>
          )}
        </div>

        <div className={`flex items-center justify-between pt-3 sm:pt-4 border-t border-gray-100 ${isMobile ? "flex-wrap gap-2" : ""}`}>
          <div className={`flex ${isMobile ? "gap-2 flex-wrap w-full" : "space-x-3"}`}>
            {session.status === 0 && (
              <>
                {!now.isBefore(sessionDate) && (
                  <Tooltip title="Start Session">
                    <Button
                      type="primary"
                      icon={<PlayCircleOutlined />}
                      onClick={() => {onStartSession(session);setToComplete(true)}}
                      size={isMobile ? "small" : "large"}
                      className={`rounded-lg bg-green-600 hover:bg-green-700 border-none ${isMobile ? "flex-1" : ""}`}
                    >
                      {isMobile ? "Start" : "Start Session"}
                    </Button>
                  </Tooltip>
                )}
                <Tooltip title="Cancel Session">
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={() => onCancel(session)}
                    size={isMobile ? "small" : "large"}
                    className={`rounded-lg ${isMobile ? "flex-1" : ""}`}
                  >
                    Cancel
                  </Button>
                </Tooltip>
              </>
            )}
            {session.status === 1 && <Tag color="red" className="text-xs">Session cancelled</Tag>}
            {session.status === 2 && <Tooltip title="Start Session">
                    <Button
                      type="primary"
                      icon={<PlayCircleOutlined />}
                      onClick={() =>{ onStartSession(session);setToComplete(false)}}
                      size={isMobile ? "small" : "large"}
                      className={`rounded-lg bg-green-600 hover:bg-green-700 border-none ${isMobile ? "flex-1" : ""}`}
                    >
                      {isMobile ? "Add notes" : "Add Notes for Session"}
                    </Button>
                  </Tooltip>}
            {session.status === 3 && <Tag color="default" className="text-xs">Session skipped</Tag>}
          </div>
        </div>
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-primary_hover/5 to-cyan-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-xl sm:rounded-2xl"></div>
    </div>
  );
});

SessionCard.displayName = "SessionCard";

const INITIAL_ROWS = Array.from({ length: 12 }, (_, i) => ({
  id: i + 1, date: "", day: "", physio: "", signature: "", notes: "",
}));

const SpecialistBookingsScreen = () => {
  const { breakpoint, isMobile, isTablet } = useResponsive();
  const [activeTab, setActiveTab] = useState("0");
  const [filtersDrawerVisible, setFiltersDrawerVisible] = useState(false);

  const [startSessionModalVisible, setStartSessionModalVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [reportRead, setReportRead] = useState(false);

  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  // Schedule table rows state
  const [scheduleRows, setScheduleRows] = useState(INITIAL_ROWS);

  const [sessionNotesForm] = Form.useForm();
  const [cancelForm] = Form.useForm();
  const [assessmentForm] = Form.useForm();
  const [scheduleForm] = Form.useForm();
const [toComplete,setToComplete]=useState(true)
  const [filters, setFilters] = useState({ patientName: "" });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: isMobile ? 4 : isTablet ? 6 : 12,
    total: 0,
  });

  const { data: sessionsData, isLoading, refetch } = usePatientSessions({
    status: parseInt(activeTab), pageNumber: pagination.current, pageSize: pagination.pageSize,
  });

  const { data: pendingCount } = usePatientSessions({ status: 0, pageNumber: 1, pageSize: 1 });
  const { data: cancelledCount } = usePatientSessions({ status: 1, pageNumber: 1, pageSize: 1 });
  const { data: completedCount } = usePatientSessions({ status: 2, pageNumber: 1, pageSize: 1 });
  const { data: skippedCount } = usePatientSessions({ status: 3, pageNumber: 1, pageSize: 1 });

  const updateSessionMutation = useUpdateSessionStatus();

  useEffect(() => {
    if (sessionsData?.totalCount) setPagination((prev) => ({ ...prev, total: sessionsData.totalCount }));
  }, [sessionsData?.totalCount]);

  const filteredSessions = useMemo(() => {
    if (!sessionsData?.sessions) return [];
    return sessionsData.sessions.filter((session) =>
      session.patientName.toLowerCase().includes(filters.patientName.toLowerCase())
    );
  }, [sessionsData?.sessions, filters.patientName]);

  const statistics = useMemo(() => ({
    pending: pendingCount?.totalCount || 0,
    cancelled: cancelledCount?.totalCount || 0,
    completed: completedCount?.totalCount || 0,
    skipped: skippedCount?.totalCount || 0,
  }), [pendingCount, cancelledCount, completedCount, skippedCount]);

  const handleRowChange = useCallback((index, field, value) => {
    setScheduleRows(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  }, []);

  const handleStartSession = useCallback(async (session,isCompleted) => {
    setCurrentSession(session);
    setToComplete(isCompleted);
    setStartSessionModalVisible(true);
    setCurrentStep(0);
    setReportRead(false);
    setLoadingReport(true);
    setReportData(null);
    setScheduleRows(INITIAL_ROWS);
    assessmentForm.resetFields();
    scheduleForm.resetFields();

    try {
      const response = await getUserReport(session.patientId);
      setReportData(response);
    } catch (error) {
      message.error("Failed to load patient report");
      console.error("Error loading report:", error);
    } finally {
      setLoadingReport(false);
    }
  }, [assessmentForm, scheduleForm]);

  const handleCancel = useCallback((session) => {
    setCurrentSession(session);
    setCancelModalVisible(true);
  }, []);

  const handleNextStep = useCallback(() => {
    if (currentStep === 0 && reportData?.reportSections?.length > 0 && !reportRead) {
      message.warning("Please confirm you have read the report");
      return;
    }
    // Step 3 is "Add Notes" (hidden form) — validate before moving to Complete
    if (currentStep === 3) {
      sessionNotesForm.validateFields().then(() => {
        setCurrentStep(prev => prev + 1);
      }).catch(() => {
        message.error("Please fill in the required fields");
      });
    } else {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, reportRead, sessionNotesForm, reportData]);

  const handlePrevStep = useCallback(() => {
    setCurrentStep(prev => prev - 1);
  }, []);

  const handleCompleteSession = useCallback(() => {
    sessionNotesForm.validateFields().then((values) => {
      updateSessionMutation.mutate(
        {
          sessionId: currentSession.sessionId,
          newStatus: 2,
          noteText: values.noteText,
          additionalNote: values.additionalNote,
        },
        {
          onSuccess: () => {
            message.success("Session completed successfully");
            setStartSessionModalVisible(false);
            sessionNotesForm.resetFields();
            assessmentForm.resetFields();
            scheduleForm.resetFields();
            setScheduleRows(INITIAL_ROWS);
            setCurrentStep(0);
            setReportRead(false);
            refetch();
          },
          onError: () => {
            message.error("Failed to complete session");
          },
        }
      );
    });
  }, [currentSession, sessionNotesForm, assessmentForm, scheduleForm, updateSessionMutation, refetch]);

  const handleCancelSubmit = useCallback(() => {
    cancelForm.validateFields().then((values) => {
      updateSessionMutation.mutate(
        { sessionId: currentSession.sessionId, newStatus: 1, cancelReason: values.cancelReason },
        {
          onSuccess: () => {
            message.success("Session cancelled. Email sent to patient.");
            setCancelModalVisible(false);
            cancelForm.resetFields();
            refetch();
          },
          onError: () => {
            message.error("Failed to cancel session");
          },
        }
      );
    });
  }, [currentSession, cancelForm, updateSessionMutation, refetch]);

  const handleResetFilters = useCallback(() => {
    setFilters({ patientName: "" });
    setPagination((prev) => ({ ...prev, current: 1 }));
    if (isMobile) setFiltersDrawerVisible(false);
  }, [isMobile]);

  const handleCloseStartSession = useCallback(() => {
    setStartSessionModalVisible(false);
    setCurrentStep(0);
    setReportRead(false);
    sessionNotesForm.resetFields();
    assessmentForm.resetFields();
    scheduleForm.resetFields();
    setScheduleRows(INITIAL_ROWS);
  }, [sessionNotesForm, assessmentForm, scheduleForm]);

  const groupedSessions = useMemo(() => {
    if (!filteredSessions.length) return {};
    return filteredSessions.reduce((groups, session) => {
      const patientId = session.profileID;
      if (!groups[patientId]) {
        groups[patientId] = { patientName: session.patientName, patientId, sessions: [] };
      }
      groups[patientId].sessions.push(session);
      return groups;
    }, {});
  }, [filteredSessions]);

  const FiltersContent = useMemo(() => (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <h3 className={`font-semibold text-gray-800 flex items-center m-0 ${isMobile ? "text-sm" : "text-md"}`}>
          <FilterOutlined className="mr-2 text-sm" />Search
        </h3>
        {isMobile && <Button icon={<MenuOutlined />} onClick={() => setFiltersDrawerVisible(false)} type="text" size="small" />}
      </div>
      <div className={`grid grid-cols-1 ${isMobile ? "gap-3" : isTablet ? "md:grid-cols-2 gap-4" : "lg:grid-cols-2 gap-4"}`}>
        <Input
          placeholder="🔍 Search patient"
          prefix={<SearchOutlined className="text-gray-400" />}
          value={filters.patientName}
          onChange={(e) => setFilters((prev) => ({ ...prev, patientName: e.target.value }))}
          allowClear
          size={isMobile ? "middle" : "large"}
          className="rounded-xl border-gray-200 hover:border-primary/90 focus:border-primary"
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={handleResetFilters}
          size={isMobile ? "middle" : "large"}
          className="rounded-xl bg-primary border-none text-white hover:bg-primary_hover transform hover:scale-105 transition-all duration-300"
          block
        >
          Reset
        </Button>
      </div>
    </div>
  ), [filters, isMobile, isTablet, handleResetFilters]);

  // ─── Step content renderer ───────────────────────────────────────────────
  const renderStepContent = () => {
    switch (currentStep) {

      // ── Step 0: Review Report ──────────────────────────────────────────────
      case 0:
        return (
          <div className="space-y-4">
            <Spin spinning={loadingReport}>
              {reportData ? (
                <div className="space-y-4">
                  <div className="p-4 bg-primary/10 rounded-lg border border-primary/60">
                    <h3 className="font-bold text-lg mb-2">{currentSession?.patientName}</h3>
                    <p className="text-sm text-gray-600">Report ID: {reportData.id}</p>
                  </div>

                  {reportData.body && (
                    <div className="p-4 bg-white rounded-lg border border-gray-300 shadow-sm max-h-96 overflow-y-auto">
                      <h4 className="font-semibold mb-3 text-gray-800 flex items-center">
                        <FileTextOutlined className="mr-2 text-primary/90" />
                        Report Summary
                      </h4>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{reportData.body}</p>
                    </div>
                  )}

                  {reportData.photoIds && reportData.photoIds.length > 0 && (
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <h4 className="font-semibold mb-3 text-gray-800">Report Images</h4>
                      <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-2 md:grid-cols-3"} gap-3`}>
                        {reportData.photoIds.map((photoId, index) => (
                          <div key={index} className="relative rounded-lg overflow-hidden border border-gray-300 bg-white shadow-sm">
                            <img
                              src={getImg(photoId)}
                              alt={`Report image ${index + 1}`}
                              className="w-full h-48 object-cover"
                              onError={(e) => { e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f0f0f0" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage%3C/text%3E%3C/svg%3E'; }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {reportData.reportSections && reportData.reportSections.length > 0 && (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      <h4 className="font-semibold text-gray-800 text-base">Report Sections</h4>
                      {reportData.reportSections.map((section, index) => (
                        <div key={section.id} className="p-4 bg-primary/10 rounded-lg border border-indigo-200 shadow-sm">
                          {section.createdAt && (
                            <p className="text-xs text-gray-500 mt-1">Created:                      Created: {formatDateTime(`${section.createdAt}Z`)}
                            </p>
                          )}
                          <div className="flex items-start justify-between mb-2">
                            <h5 className="font-semibold text-indigo-900 text-base">{index + 1}. {section.title}</h5>
                          </div>
                          {section.body && (
                            <p className="text-sm text-gray-700 whitespace-pre-wrap mb-3 leading-relaxed">{section.body}</p>
                          )}
                          {section.photoIds && section.photoIds.length > 0 && (
                            <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-2"} gap-2 mt-3`}>
                              {section.photoIds.map((photoId, photoIndex) => (
                                <div key={photoIndex} className="relative rounded-lg overflow-hidden border border-indigo-300 bg-white shadow-sm">
                                  <img
                                    src={getImg(photoId)}
                                    alt={`${section.title} - Image ${photoIndex + 1}`}
                                    className="w-full h-40 object-cover"
                                    onError={(e) => { e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f0f0f0" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage%3C/text%3E%3C/svg%3E'; }}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <Checkbox checked={reportRead} onChange={(e) => setReportRead(e.target.checked)}>
                      <span className="font-medium">I confirm that I have read and reviewed the patient report</span>
                    </Checkbox>
                  </div>
                </div>
              ) : (
                !loadingReport && (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No report data available</p>
                  </div>
                )
              )}
            </Spin>
          </div>
        );

      // ── Step 1: PT Assessment Form ─────────────────────────────────────────
      case 1:
        return (
          <div className="font-serif">
            {/* Header */}
            <div className="text-center mb-5 pb-3 border-b-2 border-gray-400">
              <h2 className="text-xl font-bold underline tracking-wide text-gray-900 leading-tight">
                Physical therapy outpatient Assessment
              </h2>
            </div>

            <Form form={assessmentForm} layout="vertical">
              {/* Name / Age / Gender / Date */}
              <div className="flex flex-wrap gap-x-5 gap-y-2 items-end pb-3 mb-3 border-b border-dashed border-gray-300">
                <Form.Item name="ptName" label={<span className="font-semibold text-sm">Name:</span>} className="flex-1 min-w-[160px] mb-0">
                  <Input className="border-0 border-b border-gray-500 rounded-none px-0 focus:shadow-none bg-transparent" />
                </Form.Item>
                <Form.Item name="ptAge" label={<span className="font-semibold text-sm">Age:</span>} className="w-14 mb-0">
                  <Input className="border-0 border-b border-gray-500 rounded-none px-0 text-center bg-transparent" />
                </Form.Item>
                <Form.Item name="ptGender" label={<span className="font-semibold text-sm">Gender:</span>} className="mb-0">
                  <Checkbox.Group>
                    <Checkbox value="M">M.</Checkbox>
                    <Checkbox value="F">F.</Checkbox>
                  </Checkbox.Group>
                </Form.Item>
                <Form.Item name="ptDate" label={<span className="font-semibold text-sm">Date:</span>} className="mb-0">
                  <Input placeholder="\ \" className="border-0 border-b border-gray-500 rounded-none w-24 px-0 bg-transparent" />
                </Form.Item>
              </div>

              {/* Diagnosis / Phone */}
              <div className="flex flex-wrap gap-x-5 gap-y-2 items-end pb-3 mb-3 border-b border-dashed border-gray-300">
                <Form.Item name="ptDiagnosis" label={<span className="font-semibold text-sm">Diagnosis:</span>} className="flex-1 min-w-[160px] mb-0">
                  <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                </Form.Item>
                <Form.Item name="ptPhone" label={<span className="font-semibold text-sm">Phone Num:</span>} className="flex-1 min-w-[140px] mb-0">
                  <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                </Form.Item>
              </div>

              {/* Past History */}
              <div className="pb-2 mb-2 border-b border-dashed border-gray-300">
                <Form.Item name="ptPastHistory" label={<span className="font-semibold text-sm">Past. History:</span>} className="mb-0">
                  <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                </Form.Item>
              </div>

              {/* Chief Complain */}
              <div className="pb-2 mb-2 border-b border-dashed border-gray-300">
                <Form.Item name="ptChiefComplain" label={<span className="font-semibold text-sm">Chief Complain:</span>} className="mb-0">
                  <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                </Form.Item>
              </div>

              {/* Job & Occupation */}
              <div className="pb-2 mb-2 border-b border-dashed border-gray-300">
                <Form.Item name="ptJob" label={<span className="font-semibold text-sm">Job & Occupation:</span>} className="mb-0">
                  <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                </Form.Item>
              </div>

              {/* Pain score / distribution / lymphatic edema */}
              <div className="flex flex-wrap gap-x-5 gap-y-2 items-end pb-3 mb-3 border-b border-dashed border-gray-300">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold whitespace-nowrap">Pain score:</span>
                  <Form.Item name="ptPainScore" className="mb-0 w-12">
                    <Input className="border-0 border-b border-gray-500 rounded-none px-0 text-center bg-transparent" />
                  </Form.Item>
                  <span className="text-sm font-semibold">/10</span>
                </div>
                <div className="flex items-center gap-2 flex-1 min-w-[120px]">
                  <span className="text-sm font-semibold whitespace-nowrap">Pain distribution:</span>
                  <Form.Item name="ptPainDistribution" className="mb-0 flex-1">
                    <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                  </Form.Item>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold whitespace-nowrap">Lymphatic edema:</span>
                  <Form.Item name="ptLymphaticEdema" className="mb-0">
                    <Checkbox.Group>
                      <Checkbox value="yes">Yes</Checkbox>
                      <Checkbox value="no">No.</Checkbox>
                    </Checkbox.Group>
                  </Form.Item>
                </div>
              </div>

              {/* Radio Investigations */}
              <div className="pb-2 mb-2 border-b border-dashed border-gray-300">
                <Form.Item name="ptRadioInvestigations" label={<span className="font-semibold text-sm">Radio Investigations:</span>} className="mb-0">
                  <TextArea
                    autoSize={{ minRows: 2 }}
                    className="border-0 border-b border-gray-400 rounded-none resize-none bg-transparent focus:shadow-none"
                  />
                </Form.Item>
              </div>

              {/* Lab Notes + Results */}
              <div className="flex flex-wrap gap-x-5 gap-y-2 items-end pb-3 mb-3 border-b border-dashed border-gray-300">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">Lab Notes:</span>
                  <Form.Item name="ptLabNotes" className="mb-0">
                    <Checkbox.Group>
                      <Checkbox value="yes">Yes</Checkbox>
                      <Checkbox value="no">No.</Checkbox>
                    </Checkbox.Group>
                  </Form.Item>
                </div>
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-sm font-semibold whitespace-nowrap">Results:</span>
                  <Form.Item name="ptLabResults" className="mb-0 flex-1">
                    <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                  </Form.Item>
                </div>
              </div>

              {/* Precautions */}
              <div className="pb-2 mb-4 border-b border-dashed border-gray-300">
                <Form.Item name="ptPrecautions" label={<span className="font-semibold text-sm">Precautions:</span>} className="mb-0">
                  <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                </Form.Item>
              </div>

              {/* Body Diagram */}
              <div className="border border-gray-300 rounded-lg p-3 mb-4 bg-gray-50">
                <div className="flex justify-center items-center gap-10 py-2">
                  {/* Front */}
                  <div className="text-center">
                    <svg viewBox="0 0 80 170" width={isMobile ? 60 : 80} height={isMobile ? 127 : 170} className="mx-auto opacity-70">
                      <ellipse cx="40" cy="11" rx="10" ry="11" fill="none" stroke="#444" strokeWidth="1.5"/>
                      <rect x="36" y="21" width="8" height="7" fill="none" stroke="#444" strokeWidth="1.5"/>
                      <path d="M24 28 Q21 48 21 70 L59 70 Q59 48 56 28 Z" fill="none" stroke="#444" strokeWidth="1.5"/>
                      <path d="M24 30 Q13 44 10 76 L16 76 Q18 48 27 36 Z" fill="none" stroke="#444" strokeWidth="1.5"/>
                      <path d="M56 30 Q67 44 70 76 L64 76 Q62 48 53 36 Z" fill="none" stroke="#444" strokeWidth="1.5"/>
                      <path d="M31 70 Q27 102 25 148 L35 148 Q35 102 37 70 Z" fill="none" stroke="#444" strokeWidth="1.5"/>
                      <path d="M49 70 Q53 102 55 148 L45 148 Q45 102 43 70 Z" fill="none" stroke="#444" strokeWidth="1.5"/>
                      <line x1="40" y1="28" x2="40" y2="70" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
                      <line x1="21" y1="48" x2="59" y2="48" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
                      <line x1="21" y1="59" x2="59" y2="59" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
                      <line x1="31" y1="70" x2="37" y2="70" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
                      <line x1="43" y1="70" x2="49" y2="70" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
                      <line x1="25" y1="110" x2="35" y2="110" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
                      <line x1="45" y1="110" x2="55" y2="110" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
                    </svg>
                    <span className="text-xs text-gray-500">Front</span>
                  </div>
                  {/* Back */}
                  <div className="text-center">
                    <svg viewBox="0 0 80 170" width={isMobile ? 60 : 80} height={isMobile ? 127 : 170} className="mx-auto opacity-70">
                      <ellipse cx="40" cy="11" rx="10" ry="11" fill="none" stroke="#444" strokeWidth="1.5"/>
                      <rect x="36" y="21" width="8" height="7" fill="none" stroke="#444" strokeWidth="1.5"/>
                      <path d="M24 28 Q21 48 21 70 L59 70 Q59 48 56 28 Z" fill="none" stroke="#444" strokeWidth="1.5"/>
                      <path d="M24 30 Q13 44 10 76 L16 76 Q18 48 27 36 Z" fill="none" stroke="#444" strokeWidth="1.5"/>
                      <path d="M56 30 Q67 44 70 76 L64 76 Q62 48 53 36 Z" fill="none" stroke="#444" strokeWidth="1.5"/>
                      <path d="M31 70 Q27 102 25 148 L35 148 Q35 102 37 70 Z" fill="none" stroke="#444" strokeWidth="1.5"/>
                      <path d="M49 70 Q53 102 55 148 L45 148 Q45 102 43 70 Z" fill="none" stroke="#444" strokeWidth="1.5"/>
                      <line x1="40" y1="28" x2="40" y2="70" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
                      <line x1="21" y1="38" x2="59" y2="38" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
                      <line x1="21" y1="50" x2="59" y2="50" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
                      <line x1="21" y1="62" x2="59" y2="62" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
                      <path d="M27 33 Q24 42 27 50" fill="none" stroke="#888" strokeWidth="1"/>
                      <path d="M53 33 Q56 42 53 50" fill="none" stroke="#888" strokeWidth="1"/>
                      <line x1="31" y1="70" x2="37" y2="70" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
                      <line x1="43" y1="70" x2="49" y2="70" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
                      <line x1="25" y1="110" x2="35" y2="110" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
                      <line x1="45" y1="110" x2="55" y2="110" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2"/>
                    </svg>
                    <span className="text-xs text-gray-500">Back</span>
                  </div>
                </div>
              </div>

              {/* PT Assessment / Plan / Sessions Num / Progress Notes table */}
              <div className="border border-gray-400 mb-4">
                <table className="w-full border-collapse text-sm">
                  <tbody>
                    <tr className="border-b border-gray-400">
                      <td className="border-r border-gray-400 p-2 font-semibold bg-gray-50 w-40 align-top whitespace-nowrap">
                        PT Assessment
                      </td>
                      <td className="p-0">
                        <Form.Item name="ptAssessment" className="mb-0">
                          <TextArea
                            autoSize={{ minRows: 3 }}
                            bordered={false}
                            className="rounded-none resize-none"
                            placeholder="Enter assessment details..."
                          />
                        </Form.Item>
                      </td>
                    </tr>
                    <tr className="border-b border-gray-400">
                      <td className="border-r border-gray-400 p-2 font-semibold bg-gray-50 align-top whitespace-nowrap">
                        PT Plan of Treatment
                      </td>
                      <td className="p-0">
                        <Form.Item name="ptPlanOfTreatment" className="mb-0">
                          <TextArea
                            autoSize={{ minRows: 3 }}
                            bordered={false}
                            className="rounded-none resize-none"
                            placeholder="Enter treatment plan..."
                          />
                        </Form.Item>
                      </td>
                    </tr>
                    <tr className="border-b border-gray-400">
                      <td className="border-r border-gray-400 p-2 font-semibold bg-gray-50 align-top whitespace-nowrap">
                        Sessions Num:
                      </td>
                      <td className="p-3">
                        <Form.Item name="ptSessionsNum" className="mb-0">
                          <Checkbox.Group className="flex flex-wrap gap-4">
                            {["3", "6", "9", "12", "18", "24", "More"].map(v => (
                              <Checkbox key={v} value={v}>{v}</Checkbox>
                            ))}
                          </Checkbox.Group>
                        </Form.Item>
                      </td>
                    </tr>
                    <tr>
                      <td className="border-r border-gray-400 p-2 font-semibold bg-gray-50 align-top whitespace-nowrap">
                        Progress Notes
                      </td>
                      <td className="p-0">
                        <Form.Item name="ptProgressNotes" className="mb-0">
                          <TextArea
                            autoSize={{ minRows: 2 }}
                            bordered={false}
                            className="rounded-none resize-none"
                          />
                        </Form.Item>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* TTT Goals / Discharge / Other Notes */}
              <div className="space-y-2">
                {[
                  { name: "ptTttGoals", label: "TTT Goals:" },
                  { name: "ptDischarge", label: "Discharge:" },
                  { name: "ptOtherNotes", label: "Any Other Notes:" },
                ].map(({ name, label }) => (
                  <div key={name} className="flex items-center gap-2 pb-1 border-b border-dashed border-gray-300">
                    <span className="text-sm font-semibold whitespace-nowrap">{label}</span>
                    <Form.Item name={name} className="mb-0 flex-1">
                      <Input bordered={false} className="border-b border-gray-400 rounded-none px-0" />
                    </Form.Item>
                  </div>
                ))}
              </div>
            </Form>
          </div>
        );

      // ── Step 2: Sessions Schedule & Follow-up Sheet ────────────────────────
      case 2:
        return (
          <div className="font-serif">
            {/* Header */}
            <div className="text-center mb-5 pb-3 border-b-2 border-gray-400">
              <h2 className="text-xl font-bold underline tracking-wide text-gray-900 leading-tight">
                Sessions Schedule &amp; Follow up Sheet
              </h2>
            </div>

            <Form form={scheduleForm} layout="vertical">
              {/* Name / Age / Gender / Date */}
              <div className="flex flex-wrap gap-x-5 gap-y-2 items-end pb-3 mb-3 border-b border-dashed border-gray-300">
                <Form.Item name="schName" label={<span className="font-semibold text-sm">Name:</span>} className="flex-1 min-w-[160px] mb-0">
                  <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent focus:shadow-none" />
                </Form.Item>
                <Form.Item name="schAge" label={<span className="font-semibold text-sm">Age:</span>} className="w-14 mb-0">
                  <Input className="border-0 border-b border-gray-500 rounded-none px-0 text-center bg-transparent" />
                </Form.Item>
                <Form.Item name="schGender" label={<span className="font-semibold text-sm">Gender:</span>} className="mb-0">
                  <Checkbox.Group>
                    <Checkbox value="M">M.</Checkbox>
                    <Checkbox value="F">F.</Checkbox>
                  </Checkbox.Group>
                </Form.Item>
                <Form.Item name="schDate" label={<span className="font-semibold text-sm">Date:</span>} className="mb-0">
                  <Input placeholder="\ \" className="border-0 border-b border-gray-500 rounded-none w-24 px-0 bg-transparent" />
                </Form.Item>
              </div>

              {/* Diagnosis / Phone */}
              <div className="flex flex-wrap gap-x-5 gap-y-2 items-end pb-3 mb-3 border-b border-dashed border-gray-300">
                <Form.Item name="schDiagnosis" label={<span className="font-semibold text-sm">Diagnosis:</span>} className="flex-1 min-w-[160px] mb-0">
                  <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                </Form.Item>
                <Form.Item name="schPhone" label={<span className="font-semibold text-sm">Phone Num:</span>} className="flex-1 min-w-[140px] mb-0">
                  <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                </Form.Item>
              </div>

              {/* Past History */}
              <div className="pb-2 mb-2 border-b border-dashed border-gray-300">
                <Form.Item name="schPastHistory" label={<span className="font-semibold text-sm">Past. History:</span>} className="mb-0">
                  <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                </Form.Item>
              </div>

              {/* Chief Complain */}
              <div className="pb-2 mb-2 border-b border-dashed border-gray-300">
                <Form.Item name="schChiefComplain" label={<span className="font-semibold text-sm">Chief Complain:</span>} className="mb-0">
                  <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                </Form.Item>
              </div>

              {/* Job & Occupation */}
              <div className="pb-2 mb-3 border-b border-dashed border-gray-300">
                <Form.Item name="schJob" label={<span className="font-semibold text-sm">Job & Occupation:</span>} className="mb-0">
                  <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                </Form.Item>
              </div>

              {/* Weekly schedule */}
              <div className="flex flex-wrap gap-x-8 items-center pb-4 mb-4 border-b border-dashed border-gray-300">
                <span className="text-sm font-semibold">Weekly:</span>
                <Form.Item name="schWeekly" className="mb-0">
                  <Checkbox.Group className="flex flex-wrap gap-3">
                    <Checkbox value="Sat">Sat</Checkbox>
                    <Checkbox value="Mon">Mon</Checkbox>
                    <Checkbox value="Wed">Wed</Checkbox>
                    <span className="mx-3 text-gray-300 font-light select-none">|</span>
                    <Checkbox value="Sun">Sun</Checkbox>
                    <Checkbox value="Tue">Tus</Checkbox>
                    <Checkbox value="Thu">Thurs</Checkbox>
                  </Checkbox.Group>
                </Form.Item>
              </div>

              {/* Sessions table */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse" style={{ minWidth: isMobile ? 480 : "auto" }}>
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-400 p-2 text-center text-xs font-bold w-8">#</th>
                      <th className="border border-gray-400 p-2 text-center text-xs font-bold w-24">Date</th>
                      <th className="border border-gray-400 p-2 text-center text-xs font-bold w-20">Day</th>
                      <th className="border border-gray-400 p-2 text-center text-xs font-bold">Physiotherapist</th>
                      <th className="border border-gray-400 p-2 text-center text-xs font-bold">Patient Signature</th>
                      <th className="border border-gray-400 p-2 text-center text-xs font-bold w-24">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduleRows.map((row, index) => (
                      <tr key={row.id}>
                        <td className="border border-gray-400 p-1 text-center text-xs font-bold text-gray-700">
                          {row.id}
                        </td>
                        <td className="border border-gray-400 p-0">
                          <Input
                            bordered={false}
                            placeholder="  /  /"
                            value={row.date}
                            onChange={e => handleRowChange(index, "date", e.target.value)}
                            className="text-center text-xs p-1 h-8"
                          />
                        </td>
                        <td className="border border-gray-400 p-0">
                          <Input
                            bordered={false}
                            value={row.day}
                            onChange={e => handleRowChange(index, "day", e.target.value)}
                            className="text-center text-xs p-1 h-8"
                          />
                        </td>
                        <td className="border border-gray-400 p-0">
                          <Input
                            bordered={false}
                            value={row.physio}
                            onChange={e => handleRowChange(index, "physio", e.target.value)}
                            className="text-xs p-1 h-8"
                          />
                        </td>
                        <td className="border border-gray-400 p-0">
                          <Input
                            bordered={false}
                            value={row.signature}
                            onChange={e => handleRowChange(index, "signature", e.target.value)}
                            className="text-xs p-1 h-8"
                          />
                        </td>
                        <td className="border border-gray-400 p-0">
                          <Input
                            bordered={false}
                            value={row.notes}
                            onChange={e => handleRowChange(index, "notes", e.target.value)}
                            className="text-xs p-1 h-8"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Signatures footer */}
              <div className="flex justify-between pt-8 mt-4 border-t border-gray-300">
                <div className="text-center">
                  <div className="w-40 border-b border-gray-500 h-8 mb-1"></div>
                  <span className="text-sm font-semibold">P.T Specialist Signature</span>
                </div>
                <div className="text-center">
                  <div className="w-40 border-b border-gray-500 h-8 mb-1"></div>
                  <span className="text-sm font-semibold">Consultant Signature</span>
                </div>
              </div>
            </Form>
          </div>
        );

      // ── Step 3: Add Notes ──────────────────────────────────────────────────
      // (Rendered via the always-mounted hidden Form below, not here)

      // ── Step 4: Complete ───────────────────────────────────────────────────
      case 4:
        return (
          <div className="space-y-4">
            <div className="text-center py-6">
              <CheckOutlined className="text-6xl text-green-500 mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">Ready to Complete Session</h3>
              <p className="text-gray-600">Please review and confirm the session completion</p>
            </div>

            <div className="p-4 bg-primary/10 rounded-lg border border-primary/60">
              <h4 className="font-semibold mb-2">Session Details</h4>
              <p><strong>Patient:</strong> {currentSession?.patientName}</p>
              <p><strong>Specialist:</strong> {currentSession?.specialistName}</p>
              <p><strong>Date:</strong> {currentSession?.sessionDate ? dayjs(currentSession.sessionDate).format("MMM D, YYYY") : "N/A"}</p>
            </div>

            {sessionNotesForm.getFieldValue("noteText") && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-semibold mb-2">Session Notes</h4>
                <p className="text-sm whitespace-pre-wrap">{sessionNotesForm.getFieldValue("noteText")}</p>
              </div>
            )}

            {sessionNotesForm.getFieldValue("additionalNote") && (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold mb-2">Additional Notes</h4>
                <p className="text-sm whitespace-pre-wrap">{sessionNotesForm.getFieldValue("additionalNote")}</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <style jsx>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        .ant-tabs-tab {
          border-width: 1px; border-style: solid; border-color: #298e9f !important;
          margin-right: ${isMobile ? "4px" : "8px"} !important;
          padding: ${isMobile ? "4px 8px" : "8px 16px"} !important;
          font-size: ${isMobile ? "12px" : "14px"} !important;
        }
      `}</style>

      <div className={`${isMobile ? "p-2" : "p-4"}`}>
        <Card className={`mb-3 sm:mb-6 shadow-sm border-0 bg-white/80 backdrop-blur-sm ${isMobile ? "px-2" : ""}`}>
          <Tabs activeKey={activeTab} onChange={setActiveTab} type="line" size={isMobile ? "small" : "large"} tabBarGutter={isMobile ? 4 : 8}>
            <TabPane tab={`⏳ Pending (${statistics.pending})`} key="0" />
            <TabPane tab={`❌ Cancelled (${statistics.cancelled})`} key="1" />
            <TabPane tab={`✅ Completed (${statistics.completed})`} key="2" />
            <TabPane tab={`⏭️ Skipped (${statistics.skipped})`} key="3" />
          </Tabs>
        </Card>

        {isMobile ? (
          <>
            <div className="mb-3">
              <Button icon={<FilterOutlined />} onClick={() => setFiltersDrawerVisible(true)}
                className="rounded-xl bg-primary border-none text-white w-full">
                Search
              </Button>
            </div>
            <Drawer title="Search" placement="bottom" closable={true} onClose={() => setFiltersDrawerVisible(false)}
              open={filtersDrawerVisible} height="auto">
              {FiltersContent}
            </Drawer>
          </>
        ) : (
          <Card className="mb-3 shadow-sm border-0 bg-white/80 backdrop-blur-sm">{FiltersContent}</Card>
        )}

        <Spin spinning={isLoading}>
          {filteredSessions.length > 0 ? (
            <>
              <div className="grid grid-cols-1 mb-6 sm:mb-8">
                {Object.values(groupedSessions).length > 0 ? (
                  <>
                    <Collapse className="mb-6" defaultActiveKey={[]} expandIconPosition="end" ghost={isMobile}>
                      {Object.values(groupedSessions).map((group) => (
                        <Collapse.Panel
                          key={group.patientId}
                          header={
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center">
                                <Avatar
                                  size={isMobile ? 32 : 40}
                                  icon={<UserOutlined />}
                                  className="bg-gradient-to-r from-primary to-primary_hover mr-3"
                                />
                                <div>
                                  <span className={`font-bold text-gray-800 ${isMobile ? "text-sm" : "text-lg"}`}>
                                    {group.patientName}
                                  </span>
                                  <div className="text-xs text-gray-500">
                                    {group.sessions.length} {group.sessions.length === 1 ? "session" : "sessions"}
                                  </div>
                                </div>
                              </div>
                              <Badge count={group.sessions.length} style={{ backgroundColor: "#298e9f" }} />
                            </div>
                          }
                          className="mb-3 bg-white rounded-xl shadow-sm"
                        >
                          <div className={`grid grid-cols-1 ${isMobile ? "gap-3" : isTablet ? "sm:grid-cols-2 gap-4" : "sm:grid-cols-2 lg:grid-cols-2 gap-4"}`}>
                            {group.sessions.map((session, index) => (
                              <SessionCard
                                key={session.sessionId}
                                session={session}
                                index={index}
                                onStartSession={handleStartSession}
                                onCancel={handleCancel}
                                isMobile={isMobile}
                                setToComplete={setToComplete}
                                setCancelModalVisible={setCancelModalVisible}
                              />
                            ))}
                          </div>
                        </Collapse.Panel>
                      ))}
                    </Collapse>
                  </>
                ) : (
                  <div className="text-center py-16">
                    <div className={`${isMobile ? "text-4xl" : "text-6xl"} mb-4`}>📋</div>
                    <h3 className={`font-bold text-gray-600 mb-2 ${isMobile ? "text-lg" : "text-2xl"}`}>No Sessions Found</h3>
                    <p className={`text-gray-500 ${isMobile ? "text-sm" : ""}`}>Try adjusting your filters</p>
                  </div>
                )}
              </div>

              <div className="flex justify-center">
                <div className={`bg-white rounded-xl sm:rounded-2xl shadow-lg ${isMobile ? "p-2" : "p-4"}`}>
                  <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={(page, pageSize) => setPagination({ ...pagination, current: page, pageSize })}
                    showTotal={(total) => (
                      <span className={`text-gray-600 font-medium ${isMobile ? "text-xs" : ""}`}>
                        {isMobile ? `${total} total` : `Total ${total} sessions`}
                      </span>
                    )}
                    size={isMobile ? "small" : "default"}
                    showSizeChanger={!isMobile}
                    showQuickJumper={!isMobile}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-16">
              <div className={`${isMobile ? "text-4xl" : "text-6xl"} mb-4`}>📋</div>
              <h3 className={`font-bold text-gray-600 mb-2 ${isMobile ? "text-lg" : "text-2xl"}`}>No Sessions Found</h3>
              <p className={`text-gray-500 ${isMobile ? "text-sm" : ""}`}>Try adjusting your filters</p>
            </div>
          )}
        </Spin>
      </div>
<StartSessionModal
  open={!!currentSession && !cancelModalVisible}
  session={currentSession}
  onClose={() => {setCurrentSession(null);setToComplete(true)}}
  onSessionComplete={() => { setCurrentSession(null); refetch(); }}
  updateSessionMutation={updateSessionMutation}
  isMobile={isMobile}
  isCompleted={toComplete}
note={currentSession?.noteText ?? null}

/>

      {/* ─── Cancel Session Modal ─────────────────────────────────────────────── */}
      <Modal
        title="Cancel Session"
        open={cancelModalVisible}
        onOk={handleCancelSubmit}
        onCancel={() => { setCancelModalVisible(false); cancelForm.resetFields(); }}
        okText="Cancel Session"
        cancelText="Go Back"
        width={isMobile ? "95vw" : 600}
        centered={isMobile}
        confirmLoading={updateSessionMutation.isPending}
        okButtonProps={{ danger: true }}
      >
        <Form form={cancelForm} layout="vertical">
          <div className="mb-4 p-4 bg-red-50 rounded-lg border border-red-200">
            <p><strong>Patient:</strong> {currentSession?.patientName}</p>
            <p><strong>Date:</strong> {currentSession?.sessionDate ? dayjs(currentSession.sessionDate).format("MMM D, YYYY") : "N/A"}</p>
            <p className="text-red-600 mt-2"><strong>Note:</strong> An email will be sent to the patient with the cancellation reason.</p>
          </div>
          <Form.Item
            name="cancelReason"
            label="Cancellation Reason"
            rules={[
              { required: true, message: "Please provide a reason for cancellation" },
              { min: 10, message: "Reason must be at least 10 characters" },
            ]}
          >
            <TextArea rows={4} placeholder="Enter the reason for cancelling this session..." className="rounded-lg" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SpecialistBookingsScreen;